import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { AriaCheckbox } from '~/components/AriaCheckbox';
import { Button } from '~/components/Button';
import { CheckboxGroup } from '~/components/CheckboxGroup';
import { Divider } from '~/components/Divider';
import { Drawer } from '~/components/Drawer';
import type { DrawerProps } from '~/components/Drawer/src/Drawer.types';
import { Box } from '~/components/Layout/Box';
import { Stack } from '~/components/Layout/Stack';
import { type BpiFilter, useBlueprintIndexStore } from '~/stores/blueprintIndexStore';
import { usePlaygroundActions } from '~/stores/playgroundStore';
import styles from './FiltersDrawer.module.css';
type FiltersDrawerProps = Omit<DrawerProps, 'children'> & {
  blueprintCount: number;
};
export function FiltersDrawer(t0) {
  const $ = _c(16);
  const {
    isOpen,
    close,
    blueprintCount
  } = t0;
  const t = useTranslations();
  let t1;
  if ($[0] !== blueprintCount || $[1] !== t) {
    t1 = t("content.blueprintIndex.filters_results", {
      count: blueprintCount
    });
    $[0] = blueprintCount;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <p className={styles.drawerTitle}>{t1}</p>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Filters />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("content.blueprintIndex.filters_apply");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== close || $[9] !== t4) {
    t5 = <Box space="--by-space-200"><Stack space="--by-space-400">{t3}<div className={styles.buttonRow}><Button onPress={close} variant="secondary">{t4}</Button></div></Stack></Box>;
    $[8] = close;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== close || $[12] !== isOpen || $[13] !== t2 || $[14] !== t5) {
    t6 = <Drawer isOpen={isOpen} close={close} title={t2}>{t5}</Drawer>;
    $[11] = close;
    $[12] = isOpen;
    $[13] = t2;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
export function FiltersPanel() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className={styles.filtersPanel}><Box space="--by-space-200"><Stack space="--by-space-400"><Filters /></Stack></Box></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
const DURATION_FILTER_TO_NIGHTS: Record<string, number> = {
  long_weekend: 3,
  // 1-4 nights → 3 nights (midpoint)
  week: 7,
  // 5-9 nights → 7 nights (midpoint)
  two_weeks: 13,
  // 10-16 nights → 13 nights (midpoint)
  longer: 20 // 17+ nights → 20 nights (representative value)
};
const DURATION_FILTERS = ['long_weekend', 'week', 'two_weeks', 'longer'] as const;
function Filters() {
  const $ = _c(10);
  const t = useTranslations();
  const {
    filters,
    setFilters
  } = useBlueprintIndexStore(_temp);
  const {
    setNights,
    setStartDate,
    setEndDate,
    setDatesTab
  } = usePlaygroundActions();
  let t0;
  if ($[0] !== setDatesTab || $[1] !== setEndDate || $[2] !== setFilters || $[3] !== setNights || $[4] !== setStartDate) {
    t0 = newFilters => {
      const filtersArray = (Array.isArray(newFilters) ? newFilters : Array.from(newFilters)) as BpiFilter[];
      setFilters(filtersArray);
      const selectedDurationFilters = filtersArray.filter(_temp2) as (typeof DURATION_FILTERS)[number][];
      if (selectedDurationFilters.length === 1) {
        const nightsValue = DURATION_FILTER_TO_NIGHTS[selectedDurationFilters[0]];
        setNights(nightsValue);
        setDatesTab("nights");
        setStartDate(null);
        setEndDate(null);
      } else {
        setNights(null);
      }
    };
    $[0] = setDatesTab;
    $[1] = setEndDate;
    $[2] = setFilters;
    $[3] = setNights;
    $[4] = setStartDate;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  const handleFiltersChange = t0;
  let t1;
  if ($[6] !== filters || $[7] !== handleFiltersChange || $[8] !== t) {
    const filterSections = [{
      id: "duration",
      name: t("content.blueprintIndex.duration_section"),
      items: [{
        name: t("content.blueprintIndex.duration_1_4"),
        value: "long_weekend"
      }, {
        name: t("content.blueprintIndex.duration_5_9"),
        value: "week"
      }, {
        name: t("content.blueprintIndex.duration_10_16"),
        value: "two_weeks"
      }, {
        name: t("content.blueprintIndex.duration_17_plus"),
        value: "longer"
      }]
    }, {
      id: "themes",
      name: t("content.blueprintIndex.themes_section"),
      items: [{
        name: t("content.blueprintIndex.theme_sleeper_train"),
        value: "jai_sleeper_train"
      }]
    }];
    t1 = <>{filterSections.map((section, index) => <CheckboxGroup id={`filter-checkbox-group-${section.name}`} key={section.name} label={section.name} onChange={handleFiltersChange} value={filters}><div className="pb-[5px]"><Divider key={`divider-${index}`} variant="horizontal" /></div>{section.items.map(item => <AriaCheckbox key={`filter-checkbox-${item.value}`} value={item.value} data-ua-click={`bpi-filter-checkbox-${section.id}-${item.value}`}>{item.name}</AriaCheckbox>)}</CheckboxGroup>)}</>;
    $[6] = filters;
    $[7] = handleFiltersChange;
    $[8] = t;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  return t1;
}
function _temp2(filter) {
  return DURATION_FILTERS.includes(filter as (typeof DURATION_FILTERS)[number]);
}
function _temp(state) {
  return state;
}